/**
 * Reason codes that mean THE DECISION ON THIS ALERT IS ALREADY MADE AND
 * RECORDED AT THE PROVIDER. Nothing is left for an operator to do: the money
 * was returned, the subscription cancelled, the alert written off as a
 * duplicate or as not ours.
 *
 * Not every one of them is a happy ending — `Error`, `Ineligible`, `MIDLost`
 * and the two `Unmatched*` are refusals — but all of them are FINAL: the
 * provider has closed the item and accepts no further status update on it
 * (see `RiskAlertSourcePort.updateAlert`, which is never retried for the same
 * reason). "Settled" here means "no longer open", not "resolved well".
 *
 * The three codes deliberately ABSENT from this list are `Unactioned`, `None`
 * and `WIP`, plus an empty/missing value. `WIP` is the one worth spelling out:
 * work was STARTED and not finished, and dropping such an alert out of the
 * operator's queue would lose it — nobody comes back to it while the
 * provider's deadline keeps running.
 *
 * How the values get there in the first place: `Resolved` is stamped by our
 * own automation (it lives in another service for now — it finds the charge,
 * refunds it and marks the alert resolved); the rest an operator sets by hand
 * in the Chargeblast admin panel. Chargeblast itself auto-handles only RDR.
 *
 * The full provider dictionary (this list plus the three open codes) is
 * mirrored as `RISK_ACTION_REASON_CODES` in `@wit-support/admin-contracts` —
 * that copy is the OPERATOR'S PICKLIST, this one is the rule; a code missing
 * here is treated as open, which is the safe direction (see below).
 */
export const TERMINAL_REASON_CODES = [
    'Resolved',
    'ResolvedPartial',
    'ResolvedCancelled',
    'AccountCancelled',
    'CancellationProcessed',
    'AlreadyRefunded',
    'AlreadyChargeback',
    'Duplicate',
    'MIDLost',
    'Ineligible',
    'TDS',
    'NotMyDescriptor',
    'EscalateChargeback',
    'CannotCancel',
    'MatchedWillShip',
    'ContactedShipper',
    'UnmatchedGeneral',
    'UnmatchedCannotFindTransaction',
    'Error',
];
/** Lower-cased index — see the normalization note on {@link isProviderSettled}. */
const TERMINAL_BY_LOWER = new Set(TERMINAL_REASON_CODES.map((c) => c.toLowerCase()));
/**
 * Has the provider already recorded a decision on this alert?
 *
 * An UNKNOWN code answers `false` — the provider adds codes on its side, and
 * treating a code we have never seen as "settled" would silently drop a live
 * alert out of the operator's queue. The asymmetry is deliberate: one extra
 * card costs a glance, a missed chargeback costs money.
 *
 * Trimmed and case-folded before lookup: the provider sends `''` where it
 * means "no value" (already normalized to `null` at the Chargeblast adapter
 * boundary, but this function is also called against RAW DB rows by the
 * backfill migration's SQL twin), and the answer must not depend on how the
 * value was spelled.
 */
export const isProviderSettled = (reasonCode) => reasonCode == null ? false : TERMINAL_BY_LOWER.has(reasonCode.trim().toLowerCase());
/**
 * Whether an alert needs a human to do something. TWO conditions, joined by
 * AND — the network must be one where the refund is ours to make, AND the
 * provider must not have recorded a decision yet.
 *
 * Lives in the CORE, and every adapter calls it rather than re-deriving the
 * rule: RDR is refunded by the acquirer on its own and TC40/SAFE are pure
 * fraud telemetry, so treating them as work would bury the operator in items
 * that need no reply — and a second copy of that rule in a second adapter
 * would drift from this one the first time a network changes sides.
 *
 * The network axis ALONE is what shipped first, and it was measurably wrong as
 * an operator signal: on prod 1354 alerts carried `actionRequired`, of which
 * only 38 were still open at the provider — the "needs action" filter showed
 * an operator 1354 cards instead of 38 and was therefore useless. The network
 * answers "does this KIND of alert require our refund at all", which is a
 * property of the network and stays true forever; whether THIS alert still
 * needs the work is a property of its `reason_code`, and only the two together
 * make a queue.
 *
 * A `null` network (one the adapter did not recognize) is conservatively
 * treated as ACTIONABLE, and so is an unrecognized reason code: an unknown
 * signal is better shown to a human than silently filed away as telemetry.
 */
export const computeActionRequired = (descriptor, network, reasonCode) => {
    if (isProviderSettled(reasonCode))
        return false;
    return network === null ? true : !descriptor.informationalNetworks.includes(network);
};
/**
 * HTTP status that arrives shaped like a 4xx but is NOT a determinate
 * rejection: 408 Request Timeout means exactly "we did not wait for an
 * answer", i.e. the outcome at the provider is unknown, same as a 5xx or a
 * dropped connection.
 */
const HTTP_REQUEST_TIMEOUT = 408;
/**
 * Was a failed `updateAlert` call a DETERMINATE rejection — the provider
 * answered, and the answer means "this decision was not accepted"?
 *
 * Only in that case is it legal to close the claiming `risk_alert_action` row
 * as `failed` and thereby free the partial unique index
 * (`risk_alert_action_one_sent`) for a retry. Every other outcome — 5xx,
 * connection resets, timeouts, and 408 specifically — means the outcome at
 * the provider is UNKNOWN: the update may have applied in full, and the
 * provider counts only the first one. Closing such a row `failed` would let a
 * second, contradicting decision reach the provider (and would silently
 * reopen the "send decision" button in the admin UI) — this is the exact
 * defect this predicate exists to prevent (see `apps/admin-api/src/routes/
 * chargebacks.ts`'s `POST /alerts/:id/action` and the worker's
 * `risk-reactor` duplicate-response branch, its only two callers).
 *
 * Lives here, not in either caller, because both need the SAME rule: two
 * independently-tuned copies would drift the moment one of them added a
 * status code the other didn't know about.
 */
export const isDeterminateRejection = (err) => {
    const status = err?.status;
    return (typeof status === 'number' && status >= 400 && status < 500 && status !== HTTP_REQUEST_TIMEOUT);
};
/**
 * The no-op matcher used while `PAYMENT_API_BASE_URL` is unset. Answers
 * `unavailable` rather than throwing, because a missing endpoint is an
 * expected steady state here — the module works without identification, an
 * operator simply binds customers by hand.
 */
export const createUnconfiguredMatcher = () => ({
    match: async () => ({ status: 'unavailable', reason: 'not_configured' }),
});
//# sourceMappingURL=ports.js.map